import React, { useEffect, useRef, useState } from 'react'
import apiClient, { API_ENDPOINTS, getUserInfoSync } from '../../config/api'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import Swal from 'sweetalert2'

const formatNumber = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) {
    return '0.00'
  }
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatCurrency = (value) => `$${formatNumber(value)}`
const formatPercentage = (value) => `${(Number(value ?? 0)).toFixed(2)}%`

const formatDateForApi = (date) => date.toISOString().split('T')[0]

const deriveDateRange = (currentFilters) => {
  if (currentFilters.range === 'custom') {
    return {
      startDate: currentFilters.startDate,
      endDate: currentFilters.endDate
    }
  }

  const today = new Date()
  const endDate = formatDateForApi(today)
  const startDate = new Date(today)

  switch (currentFilters.range) {
    case 'last_90_days':
      startDate.setDate(startDate.getDate() - 90)
      break
    case 'year_to_date':
      startDate.setMonth(0, 1)
      break
    case 'fiscal_year':
      startDate.setMonth(6, 1) // July 1st (example fiscal year start)
      if (today < startDate) {
        startDate.setFullYear(startDate.getFullYear() - 1)
      }
      break
    case 'last_30_days':
    default:
      startDate.setDate(startDate.getDate() - 30)
      break
  }

  return {
    startDate: formatDateForApi(startDate),
    endDate
  }
}

const normalizeProfitLossResponse = (data, startDate, endDate) => {
  if (!data) {
    return null
  }

  const totalRevenue = Number(data.total_revenue ?? 0)
  const totalCOGS = Number(data.total_cost_of_goods ?? 0)
  const totalExpenses = Number(data.total_expenses ?? 0)
  const grossProfit = Number(data.gross_profit ?? totalRevenue - totalCOGS)
  const netProfit = Number(data.net_profit ?? grossProfit - totalExpenses)
  const profitMargin = totalRevenue ? (netProfit / totalRevenue) * 100 : 0

  return {
    overall_summary: {
      total_revenue: totalRevenue,
      cost_of_goods_sold: totalCOGS,
      gross_profit: grossProfit,
      operating_expenses: totalExpenses,
      net_profit: netProfit,
      profit_margin: profitMargin
    },
    period_summary: [
      {
        period: `${startDate} → ${endDate}`,
        revenue: totalRevenue,
        expenses: totalCOGS + totalExpenses,
        profit: netProfit,
        margin: profitMargin
      }
    ],
    revenue_breakdown: [],
    expense_breakdown: [],
    branch_summary: []
  }
}

const dateRanges = [
  { label: 'Last 30 Days', value: 'last_30_days' },
  { label: 'Last 90 Days', value: 'last_90_days' },
  { label: 'Year to Date', value: 'year_to_date' },
  { label: 'This Fiscal Year', value: 'fiscal_year' },
  { label: 'Custom Range', value: 'custom' }
]

const ProfitnLoss = () => {
  const userInfo = getUserInfoSync()
  const companyId = userInfo?.company?.id ?? userInfo?.company_id ?? null
  const [filters, setFilters] = useState({
    range: 'last_30_days',
    startDate: '',
    endDate: ''
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reportData, setReportData] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [branches, setBranches] = useState([])
  const [branchesLoading, setBranchesLoading] = useState(false)
  const [selectedBranch, setSelectedBranch] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [reportRange, setReportRange] = useState({ startDate: '', endDate: '' })
  const reportRef = useRef(null)

  useEffect(() => {
    const fetchBranches = async () => {
      setBranchesLoading(true)
      try {
        const response = await apiClient.get(API_ENDPOINTS.BRANCHES)
        const data = response.data?.results ?? response.data ?? []
        setBranches(Array.isArray(data) ? data : [])
      } catch (err) {
        console.error('Error fetching branches:', err)
      } finally {
        setBranchesLoading(false)
      }
    }

    fetchBranches()
  }, [])

  const fetchReport = async (overrideFilters) => {
    const activeFilters = overrideFilters || filters

    if (activeFilters.range === 'custom' && (!activeFilters.startDate || !activeFilters.endDate)) {
      Swal.fire({
        icon: 'warning',
        title: 'Select Dates',
        text: 'Please select both a start and end date for the custom range.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    setLoading(true)
    setError(null)
    try {
      const { startDate, endDate } = deriveDateRange(activeFilters)

      if (!companyId) {
        setError('No company is associated with this user. Please contact support.')
        setLoading(false)
        return
      }

      const params = {
        start_date: startDate,
        end_date: endDate,
        company: companyId
      }

      if (selectedBranch) {
        params.branch_id = selectedBranch
      }

      const response = await apiClient.get('/api/accounting/profit-loss/', {
        params
      })
      setReportData(normalizeProfitLossResponse(response.data, startDate, endDate))
      setLastUpdated(new Date())
      setReportRange({ startDate, endDate })
    } catch (err) {
      console.error('Error fetching profit & loss report:', err)
      setError('Failed to load profit & loss report. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRangeChange = (event) => {
    const value = event.target.value
    setFilters((prev) => ({
      ...prev,
      range: value,
      ...(value !== 'custom' ? { startDate: '', endDate: '' } : {})
    }))
  }

  const handleDateChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleGenerateReport = (event) => {
    event.preventDefault()
    fetchReport()
  }

  const handleDownloadPdf = async () => {
    if (!reportData || !reportRef.current) {
      Swal.fire({
        icon: 'info',
        title: 'No data yet',
        text: 'Run the report first before downloading.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    setDownloading(true)
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      })
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = 10
      const printableWidth = pageWidth - margin * 2
      const imgHeight = (canvas.height * printableWidth) / canvas.width

      let heightLeft = imgHeight
      let position = margin
      let offset = 0

      pdf.addImage(imgData, 'PNG', margin, position, printableWidth, imgHeight)
      heightLeft -= pageHeight - margin * 2

      while (heightLeft > 0) {
        offset += pageHeight - margin * 2
        pdf.addPage()
        pdf.addImage(imgData, 'PNG', margin, margin - offset, printableWidth, imgHeight)
        heightLeft -= pageHeight - margin * 2
      }

      const filename = `profit-loss-${reportRange.startDate || 'report'}.pdf`
      pdf.save(filename)
    } catch (err) {
      console.error('Error generating PDF:', err)
      Swal.fire({
        icon: 'error',
        title: 'Download failed',
        text: 'Unable to create the PDF report. Please try again.',
        confirmButtonColor: '#d33'
      })
    } finally {
      setDownloading(false)
    }
  }

  const summary = reportData?.overall_summary || {}
  const periodSummary = reportData?.period_summary || reportData?.monthly_summary || []

  const hasData =
    Boolean(reportData) &&
    (Object.keys(summary).length > 0 || periodSummary.length > 0)

  const renderPageEmptyState = () => (
    <div className="text-center py-5 text-muted">
      <i className="bi bi-clipboard-data fs-1 d-block mb-3"></i>
      <p className="mb-1">No profit &amp; loss data available for the selected period.</p>
      <p>Try adjusting the filters and run the report again.</p>
    </div>
  )

  const renderTablePlaceholder = (message) => (
    <div className="text-center py-4 text-muted">
      <i className="bi bi-info-circle fs-4 d-block mb-2"></i>
      <span>{message}</span>
    </div>
  )

  const SummaryCard = ({ title, value, subtitle, accent }) => (
    <div className="col-md-4 col-lg-2 col-12">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body text-center">
          <h6 className="text-muted mb-2">{title}</h6>
          <h4 className={`fw-semibold mb-0 ${accent || ''}`}>{value}</h4>
          {subtitle && <small className="text-muted d-block mt-1">{subtitle}</small>}
        </div>
      </div>
    </div>
  )

  return (
    <>
      <div className="pagetitle">
        <h1>Profit &amp; Loss</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Reports</li>
            <li className="breadcrumb-item active">Profit &amp; Loss</li>
          </ol>
        </nav>
      </div>

      <section className="section" ref={reportRef}>
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <form className="row g-3 align-items-end" onSubmit={handleGenerateReport}>
              <div className="col-lg-3 col-md-4">
                <label className="form-label">Date Range</label>
                <select className="form-select" value={filters.range} onChange={handleRangeChange}>
                  {dateRanges.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-lg-3 col-md-4">
                <label className="form-label">Branch</label>
                <select
                  className="form-select"
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  disabled={branchesLoading}
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch.id || branch.branch_id} value={branch.id || branch.branch_id}>
                      {branch.name || branch.branch_name || branch.label || 'Unnamed Branch'}
                    </option>
                  ))}
                </select>
              </div>

              {filters.range === 'custom' && (
                <>
                  <div className="col-lg-3 col-md-4">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.startDate}
                      onChange={(e) => handleDateChange('startDate', e.target.value)}
                    />
                  </div>
                  <div className="col-lg-3 col-md-4">
                    <label className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={filters.endDate}
                      onChange={(e) => handleDateChange('endDate', e.target.value)}
                    />
                  </div>
                </>
              )}

              <div className="col-lg-2 col-md-4 d-grid">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-repeat me-2"></i>
                      Run Report
                    </>
                  )}
                </button>
              </div>

              <div className="col-lg-2 col-md-4 d-grid">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleDownloadPdf}
                  disabled={loading || downloading || !hasData}
                >
                  {downloading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Preparing PDF...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-file-earmark-arrow-down me-2"></i>
                      Download PDF
                    </>
                  )}
                </button>
              </div>

              {lastUpdated && (
                <div className="col-12">
                  <small className="text-muted">
                    Last updated {lastUpdated.toLocaleDateString()} at {lastUpdated.toLocaleTimeString()}
                  </small>
                </div>
              )}
            </form>
          </div>
        </div>

        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {!loading && !error && !hasData && renderPageEmptyState()}

        {!loading && !error && hasData && (
          <>
            <div className="row g-3 mb-4">
              <SummaryCard
                title="Total Revenue"
                value={formatCurrency(summary.total_revenue)}
                accent="text-success"
              />
              <SummaryCard
                title="Cost of Goods Sold"
                value={formatCurrency(summary.cost_of_goods_sold)}
                accent="text-danger"
              />
              <SummaryCard
                title="Gross Profit"
                value={formatCurrency(summary.gross_profit)}
              />
              <SummaryCard
                title="Operating Expenses"
                value={formatCurrency(summary.operating_expenses)}
              />
              <SummaryCard
                title="Net Profit"
                value={formatCurrency(summary.net_profit)}
                accent={Number(summary.net_profit ?? 0) >= 0 ? 'text-success' : 'text-danger'}
              />
              <SummaryCard
                title="Profit Margin"
                value={formatPercentage(summary.profit_margin)}
              />
            </div>

            <IncomeStatementFlow summary={summary} />

            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h5 className="card-title">Period Performance</h5>
                {periodSummary.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Period</th>
                          <th className="text-end">Revenue</th>
                          <th className="text-end">Expenses</th>
                          <th className="text-end">Profit</th>
                          <th className="text-end">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {periodSummary.map((period, idx) => {
                          const revenue = period.revenue ?? period.total_revenue ?? 0
                          const expenses = period.expenses ?? period.total_expenses ?? period.costs ?? 0
                          const profit = period.profit ?? period.net_profit ?? (revenue - expenses)
                          const marginValue = period.margin ?? period.profit_margin ?? (revenue ? (profit / revenue) * 100 : 0)
                          return (
                            <tr key={period.id || idx}>
                              <td>{period.label || period.period || period.month || period.date || `Period ${idx + 1}`}</td>
                              <td className="text-end">{formatCurrency(revenue)}</td>
                              <td className="text-end">{formatCurrency(expenses)}</td>
                              <td className="text-end fw-semibold">{formatCurrency(profit)}</td>
                              <td className="text-end">
                                <span className={`badge ${Number(marginValue) >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                  {formatPercentage(marginValue)}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : renderTablePlaceholder('No period performance data available.')}
              </div>
            </div>
          </>
        )}
      </section>
    </>
  )
}

const IncomeStatementFlow = ({ summary }) => {
  const steps = [
    {
      label: 'Total Revenue',
      value: formatCurrency(summary.total_revenue),
      badge: '+',
      badgeVariant: 'bg-success',
      helper: 'Top line'
    },
    {
      label: 'Cost of Goods Sold',
      value: formatCurrency(summary.cost_of_goods_sold),
      badge: '−',
      badgeVariant: 'bg-danger',
      helper: 'Direct costs'
    },
    {
      label: 'Gross Profit',
      value: formatCurrency(summary.gross_profit),
      badge: '=',
      badgeVariant: 'bg-primary',
      helper: 'Revenue minus COGS'
    },
    {
      label: 'Operating Expenses',
      value: formatCurrency(summary.operating_expenses),
      badge: '−',
      badgeVariant: 'bg-warning text-dark',
      helper: 'Overheads'
    },
    {
      label: 'Net Profit',
      value: formatCurrency(summary.net_profit),
      badge: '=',
      badgeVariant: Number(summary.net_profit ?? 0) >= 0 ? 'bg-success' : 'bg-danger',
      helper: 'Bottom line'
    }
  ]

  return (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-body">
        <h5 className="card-title mb-4">Income Statement Overview</h5>
        <div className="d-flex flex-column gap-3">
          {steps.map((step) => (
            <div key={step.label} className="d-flex align-items-center">
              <div
                className={`badge rounded-pill me-3 ${step.badgeVariant}`}
                style={{ width: 40, height: 40, fontSize: '1.1rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {step.badge}
              </div>
              <div className="flex-grow-1">
                <div className="fw-semibold">{step.label}</div>
                <small className="text-muted">{step.helper}</small>
              </div>
              <div className="fs-5 fw-semibold text-end" style={{ minWidth: 160 }}>
                {step.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ProfitnLoss

